const CALLBACK_PATH = "/shopify/customer-account/callback";
const SCOPE = "openid email customer-account-api:full";

const VERIFIER_KEY = "spwn.shopifyCustomerAccount.verifier";
const ACCESS_TOKEN_KEY = "spwn.shopifyCustomerAccount.accessToken";
const REFRESH_TOKEN_KEY = "spwn.shopifyCustomerAccount.refreshToken";
const EXPIRES_AT_KEY = "spwn.shopifyCustomerAccount.expiresAt";

const STATE_KEY = "spwn.shopifyCustomerAccount.state";
const CHALLENGE_KEY = "spwn.shopifyCustomerAccount.challenge";
const PENDING_KEY = "spwn.shopifyCustomerAccount.pending";
const RESULT_KEY = "spwn.shopifyCustomerAccount.result";
const WINDOW_NAME_KEY = "spwn.shopifyCustomerAccount.windowName";
const NOTICE_KEY = "spwn.shopifyCustomerAccount.notice";

/**
 * 購入クリックで開く窓の名前の接頭辞。認可からチェックアウトまで同じ窓を使い回すため、
 * Checkout Kit の target にもその窓の名前を渡す。
 * 名前はクリックごとに変える。固定だと、前のチェックアウトの窓を使い回してしまい、
 * 前の <shopify-checkout> を消したときに Checkout Kit がその窓を閉じてしまう。
 */
const LIVE_COMMERCE_CHECKOUT_WINDOW_PREFIX = "spwnShopifyLiveCommerceCheckout";

/** 認可の結果を待つ上限。窓が閉じたように見えても、これまでは結果が届くのを待つ */
export const AUTHORIZE_TIMEOUT_MS = 10 * 60_000;

const toBase64Url = (bytes: Uint8Array): string => {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
};

export const createCodeVerifier = (): string =>
  toBase64Url(crypto.getRandomValues(new Uint8Array(32)));

export const createCodeChallenge = async (
  verifier: string
): Promise<string> => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier)
  );
  return toBase64Url(new Uint8Array(digest));
};

/** Shopify Customer Account API の callback。page origin をそのまま使う（HTTPSプロキシ経由なら https） */
export const customerAccountRedirectUri = (origin: string): string =>
  `${origin}${CALLBACK_PATH}`;

const customerAccountConfig = ():
  | { shopId: string; clientId: string }
  | undefined => {
  const shopId = process.env.REACT_APP_SPWN_EC_SHOPIFY_SHOP_ID ?? "";
  const clientId =
    process.env.REACT_APP_SPWN_EC_SHOPIFY_CUSTOMER_ACCOUNT_CLIENT_ID ?? "";
  if (shopId === "" || clientId === "") return undefined;
  return { shopId, clientId };
};

export const buildCustomerAccountAuthorizeUrl = (input: {
  shopId: string;
  clientId: string;
  redirectUri: string;
  state: string;
  codeChallenge: string;
  promptNone: boolean;
}): string => {
  const url = new URL(
    `https://shopify.com/authentication/${input.shopId}/oauth/authorize`
  );
  url.searchParams.set("client_id", input.clientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("redirect_uri", input.redirectUri);
  url.searchParams.set("scope", SCOPE);
  url.searchParams.set("state", input.state);
  url.searchParams.set("code_challenge", input.codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  if (input.promptNone) url.searchParams.set("prompt", "none");
  return url.toString();
};

type TokenResponse = {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
};

export const exchangeCustomerAccountCode = async (input: {
  shopId: string;
  clientId: string;
  redirectUri: string;
  code: string;
  codeVerifier: string;
  fetch?: typeof fetch;
}): Promise<
  { accessToken: string; refreshToken: string; expiresIn: number } | undefined
> => {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: input.clientId,
    redirect_uri: input.redirectUri,
    code: input.code,
    code_verifier: input.codeVerifier,
  });
  return postToken(input.shopId, body, input.fetch);
};

const refreshCustomerAccountToken = async (input: {
  shopId: string;
  clientId: string;
  refreshToken: string;
  fetch?: typeof fetch;
}): Promise<
  { accessToken: string; refreshToken: string; expiresIn: number } | undefined
> => {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: input.clientId,
    refresh_token: input.refreshToken,
  });
  return postToken(input.shopId, body, input.fetch);
};

const postToken = async (
  shopId: string,
  body: URLSearchParams,
  fetchImpl: typeof fetch | undefined
): Promise<
  { accessToken: string; refreshToken: string; expiresIn: number } | undefined
> => {
  const response = await (fetchImpl ?? fetch)(
    `https://shopify.com/authentication/${shopId}/oauth/token`,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    }
  );
  if (!response.ok) return undefined;
  const json = (await response.json()) as TokenResponse;
  if (json.access_token === undefined || json.refresh_token === undefined) {
    return undefined;
  }
  return {
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresIn: json.expires_in ?? 3600,
  };
};

const readSession = (
  storage: Pick<Storage, "getItem">,
  key: string
): string | undefined => storage.getItem(key) ?? undefined;

const hasStoredCustomerCredential = (
  storage: Pick<Storage, "getItem">
): boolean =>
  storage.getItem(ACCESS_TOKEN_KEY) !== null ||
  storage.getItem(REFRESH_TOKEN_KEY) !== null;

export const loadCustomerAccessToken = async (
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem">,
  now = Date.now()
): Promise<string | undefined> => {
  const config = customerAccountConfig();
  const accessToken = readSession(storage, ACCESS_TOKEN_KEY);
  const expiresAt = Number(storage.getItem(EXPIRES_AT_KEY) ?? "0");
  if (accessToken !== undefined && expiresAt > now + 60_000) return accessToken;
  if (config === undefined) return undefined;

  const refreshToken = readSession(storage, REFRESH_TOKEN_KEY);
  if (refreshToken === undefined) return undefined;
  const refreshed = await refreshCustomerAccountToken({
    ...config,
    refreshToken,
  });
  if (refreshed === undefined) {
    clearCustomerAccountTokens(storage);
    return undefined;
  }
  saveCustomerAccountTokens(storage, refreshed, now);
  return refreshed.accessToken;
};

const saveCustomerAccountTokens = (
  storage: Pick<Storage, "setItem">,
  tokens: { accessToken: string; refreshToken: string; expiresIn: number },
  now = Date.now()
): void => {
  storage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  storage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  storage.setItem(EXPIRES_AT_KEY, String(now + tokens.expiresIn * 1000));
};

const clearCustomerAccountTokens = (
  storage: Pick<Storage, "removeItem">
): void => {
  storage.removeItem(ACCESS_TOKEN_KEY);
  storage.removeItem(REFRESH_TOKEN_KEY);
  storage.removeItem(EXPIRES_AT_KEY);
};

type CustomerAccountCallback =
  | { kind: "code"; code: string; state: string }
  | { kind: "retry" }
  /**
   * code が無い（拒否・エラー）か、state が合わない（別のタブの認可で上書きされた）。
   * 認可は失敗として、その state の持ち主へ伝える
   */
  | { kind: "failed"; state: string }
  /** state が付いていない。誰にも伝えられない */
  | { kind: "done" };

export const readCustomerAccountCallback = (
  search: string,
  storage: Pick<Storage, "getItem" | "setItem">
): CustomerAccountCallback => {
  const params = new URLSearchParams(search);
  const state = params.get("state") ?? "";
  const expected = storage.getItem(STATE_KEY) ?? "";
  if (state === "") return { kind: "done" };
  // code は state が一致したときしか渡さない。合わなければ失敗とだけ伝える
  if (state !== expected) return { kind: "failed", state };

  const error = params.get("error");
  if (
    error === "login_required" &&
    storage.getItem(PENDING_KEY) === "prompt-none"
  ) {
    return { kind: "retry" };
  }

  const code = params.get("code");
  if (error === null && code !== null && code !== "") {
    return { kind: "code", code, state };
  }
  return { kind: "failed", state };
};

type CustomerAccountResult = { state: string } & (
  | { kind: "code"; code: string }
  | { kind: "failed" }
);

/**
 * 認可窓から、配信タブへ結果を伝える（storage イベント経由）。
 * 同時に走る別の認可の結果と取り違えないよう、state を付ける
 */
export const publishCustomerAccountResult = (
  storage: Pick<Storage, "setItem" | "removeItem">,
  result: CustomerAccountResult
): void => {
  storage.setItem(RESULT_KEY, JSON.stringify(result));
  storage.removeItem(PENDING_KEY);
};

const parseCustomerAccountResult = (
  value: string | null
): CustomerAccountResult | undefined => {
  if (value === null) return undefined;
  try {
    const parsed = JSON.parse(value) as Partial<CustomerAccountResult> & {
      code?: unknown;
    };
    if (typeof parsed.state !== "string" || parsed.state === "") {
      return undefined;
    }
    if (parsed.kind === "failed") {
      return { kind: "failed", state: parsed.state };
    }
    if (parsed.kind === "code" && typeof parsed.code === "string") {
      return { kind: "code", code: parsed.code, state: parsed.state };
    }
  } catch {
    // 壊れた値は結果が無いものとして扱う
  }
  return undefined;
};

/**
 * 配信タブから認可窓へ「この窓はもう使えない」と伝える。
 * Shopify のログイン画面が COOP で opener との関係を切ると、配信タブは窓を操作できない。
 * その窓にはメッセージを出して、利用者に閉じてもらう
 */
const publishWindowLostNotice = (state: string): void => {
  localStorage.setItem(NOTICE_KEY, JSON.stringify({ state, at: Date.now() }));
};

/** `publishWindowLostNotice` が、この state の窓へのものか */
export const isWindowLostNoticeFor = (
  value: string | null,
  state: string
): boolean => {
  if (value === null) return false;
  try {
    return (JSON.parse(value) as { state?: unknown }).state === state;
  } catch {
    return false;
  }
};

export const WINDOW_LOST_NOTICE_KEY = NOTICE_KEY;

/** 認可窓の名前を、callback のページが付け直すために読む（state が一致するものだけ） */
export const readCheckoutWindowName = (
  storage: Pick<Storage, "getItem">,
  state: string
): string | undefined => {
  try {
    const parsed = JSON.parse(storage.getItem(WINDOW_NAME_KEY) ?? "null") as {
      state?: unknown;
      name?: unknown;
    } | null;
    return parsed?.state === state && typeof parsed.name === "string"
      ? parsed.name
      : undefined;
  } catch {
    return undefined;
  }
};

export const retryCustomerAccountAuthorizeUrl = (
  origin: string,
  storage: Pick<Storage, "getItem" | "setItem">
): string | undefined => {
  const config = customerAccountConfig();
  const state = storage.getItem(STATE_KEY);
  const codeChallenge = storage.getItem(CHALLENGE_KEY);
  if (config === undefined || state === null || codeChallenge === null) {
    return undefined;
  }
  storage.setItem(PENDING_KEY, "interactive");
  return buildCustomerAccountAuthorizeUrl({
    ...config,
    redirectUri: customerAccountRedirectUri(origin),
    state,
    codeChallenge,
    promptNone: false,
  });
};

/**
 * 購入クリックの中で、最初の await より前に呼ぶ。
 * popup はユーザー操作の中でしか開けないので、認可・同期のあとにチェックアウトを
 * 開くために、ここで開いた窓を最後まで使い回す。
 * 窓の名前はクリックごとに変える（接頭辞のコメントを参照）。
 */
export const openLiveCommerceCheckoutWindow = (): {
  popup: Window;
  name: string;
} | null => {
  const name = `${LIVE_COMMERCE_CHECKOUT_WINDOW_PREFIX}-${crypto.randomUUID()}`;
  const popup = window.open("about:blank", name, "popup,width=480,height=720");
  return popup === null ? null : { popup, name };
};

type CustomerAccess =
  /** トークンが使える。窓もそのまま使える */
  | "granted"
  /** トークンは取れたが、配信タブから窓を操作できない（COOP・配信タブの再読み込みなど） */
  | "detached"
  /** 認可できなかった。購入者情報なしで進める */
  | "denied"
  /** 結果が届かないまま、待つのをやめた（上限・中断）。購入をやめたとみなす */
  | "closed";

/** 保存済みのトークンがあれば使い、なければ渡された窓で認可する */
export const ensureCustomerAccess = async (
  popup: Window,
  signal?: AbortSignal
): Promise<CustomerAccess> => {
  if (!hasStoredCustomerCredential(sessionStorage)) {
    return authorizeCustomerAccountInWindow(popup, signal);
  }
  const token = await loadCustomerAccessToken(sessionStorage);
  return token === undefined ? "denied" : "granted";
};

/**
 * 渡された窓で認可し、code を localStorage で受けて PKCE 交換する。
 * 窓は閉じない。開いた側が、そのままチェックアウトに使う。
 */
const authorizeCustomerAccountInWindow = async (
  popup: Window,
  signal: AbortSignal | undefined
): Promise<CustomerAccess> => {
  const config = customerAccountConfig();
  if (config === undefined) return "denied";

  const verifier = createCodeVerifier();
  const codeChallenge = await createCodeChallenge(verifier);
  const state = crypto.randomUUID();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  localStorage.setItem(STATE_KEY, state);
  localStorage.setItem(CHALLENGE_KEY, codeChallenge);
  localStorage.setItem(PENDING_KEY, "prompt-none");
  localStorage.setItem(
    WINDOW_NAME_KEY,
    JSON.stringify({ state, name: popup.name })
  );

  popup.location.assign(
    buildCustomerAccountAuthorizeUrl({
      ...config,
      redirectUri: customerAccountRedirectUri(window.location.origin),
      state,
      codeChallenge,
      promptNone: true,
    })
  );

  const result = await waitForCustomerAccountResult(state, signal);
  if (result === undefined) {
    sessionStorage.removeItem(VERIFIER_KEY);
    return "closed";
  }
  localStorage.removeItem(RESULT_KEY);
  // 結果が届いたのに窓が閉じて見えるなら、配信タブからはもう窓を使えない
  const detached = popup.closed;
  if (detached) publishWindowLostNotice(state);
  if (result.kind === "failed") {
    sessionStorage.removeItem(VERIFIER_KEY);
    return "denied";
  }

  const tokens = await exchangeCustomerAccountCode({
    ...config,
    redirectUri: customerAccountRedirectUri(window.location.origin),
    code: result.code,
    codeVerifier: verifier,
  });
  sessionStorage.removeItem(VERIFIER_KEY);
  if (tokens === undefined) return "denied";
  saveCustomerAccountTokens(sessionStorage, tokens);
  return detached ? "detached" : "granted";
};

/**
 * この state の結果が届くまで待つ。上限か中断なら undefined。
 * `popup.closed` では打ち切らない。Shopify のログイン画面が COOP で opener との関係を切ると、
 * 窓が開いたままでも `closed` が true に見える。結果は localStorage 経由で届く
 */
const waitForCustomerAccountResult = async (
  state: string,
  signal: AbortSignal | undefined
): Promise<CustomerAccountResult | undefined> =>
  new Promise((resolve) => {
    let settled = false;
    const finish = (result: CustomerAccountResult | undefined) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("storage", onStorage);
      window.clearInterval(timer);
      window.clearTimeout(timeout);
      signal?.removeEventListener("abort", onAbort);
      resolve(result);
    };
    const accept = (value: string | null) => {
      const result = parseCustomerAccountResult(value);
      // 同時に走る別の認可の結果は受け取らない
      if (result?.state === state) finish(result);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === RESULT_KEY) accept(event.newValue);
    };
    const onAbort = () => finish(undefined);
    // storage イベントを取りこぼしても拾えるように、定期的にも見る
    const timer = window.setInterval(() => {
      accept(localStorage.getItem(RESULT_KEY));
    }, 500);
    const timeout = window.setTimeout(
      () => finish(undefined),
      AUTHORIZE_TIMEOUT_MS
    );
    window.addEventListener("storage", onStorage);
    if (signal?.aborted === true) {
      onAbort();
    } else {
      signal?.addEventListener("abort", onAbort);
    }
  });
